/* @refresh reload */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { onGameStateUpdated, useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  Game,
  SocketEvent,
  GameListResponse,
  CreateGameRequest,
  CreateGameResponse,
  CreateRoundRequest,
  CreateRoundResponse,
  RoundActionRequest,
  RoundActionResponse,
  UpdateRoundPlayerRequest,
  UpdateRoundPlayerResponse,
  EditRoundRequest,
  JoinGameResponse,
  SelectGameRequest,
  SelectGameResponse,
  EndGameRequest,
  EndGameResponse,
  GameFinalScores,
  ToggleClientDebugRequest,
  ToggleClientDebugResponse,
  UpdateEffectSettingsRequest,
  AdminPreviewEffectRequest,
  GameStartRequest,
  GameStartResponse,
  GameStateUpdatedBroadcast,
  DeleteGameRequest,
  DeleteGameResponse,
  isTerminal,
} from '@swtorbit/shared-socket-events';
import { gameLogger } from '../utils/logger';

// localStorage key for persisting selected game (its database id)
const SELECTED_GAME_STORAGE_KEY = 'swtorbit-admin-selected-game';

/** The remembered game's database id, or null. Older builds stored a room code; that is discarded. */
function readSelectedGameId(): number | null {
  try {
    const raw = localStorage.getItem(SELECTED_GAME_STORAGE_KEY);
    if (raw === null) return null;
    const id = /^\d+$/.test(raw) ? Number(raw) : NaN;
    if (Number.isSafeInteger(id) && id > 0) return id;
    localStorage.removeItem(SELECTED_GAME_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing remembered.
  }
  return null;
}

function storeSelectedGameId(gameId: number | null): void {
  try {
    if (gameId === null) localStorage.removeItem(SELECTED_GAME_STORAGE_KEY);
    else localStorage.setItem(SELECTED_GAME_STORAGE_KEY, String(gameId));
  } catch {
    // Storage unavailable — selection just isn't remembered.
  }
}

interface GameContextType {
  games: Game[];
  selectedGame: Game | null;
  error: string | null;
  isLoading: boolean;
  finalScores: Map<string, GameFinalScores>;
  clearError: () => void;
  selectGame: (game: Game | null) => void;
  createGame: (request: CreateGameRequest) => void;
  createRound: (request: CreateRoundRequest) => void;
  startRound: (request: RoundActionRequest) => void;
  endRound: (request: RoundActionRequest) => void;
  cancelRound: (request: RoundActionRequest) => void;
  disableRound: (request: RoundActionRequest) => void;
  forceResults: (request: RoundActionRequest) => void;
  editRound: (request: EditRoundRequest) => void;
  loadRound: (request: RoundActionRequest) => void;
  unloadRound: (request: RoundActionRequest) => void;
  deleteRound: (request: RoundActionRequest) => void;
  updateRoundPlayer: (request: UpdateRoundPlayerRequest) => void;
  endGame: (request: EndGameRequest) => void;
  startGame: (request: GameStartRequest) => void;
  deleteGame: (request: DeleteGameRequest) => void;
  refreshGames: () => void;
  toggleClientDebug: (request: ToggleClientDebugRequest) => void;
  updateEffectSettings: (request: UpdateEffectSettingsRequest) => void;
  previewEffect: (request: AdminPreviewEffectRequest) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

interface GameProviderProps {
  children: ReactNode;
  isAuthenticated: boolean;
}

export function GameProvider({ children, isAuthenticated }: GameProviderProps) {
  const { socket } = useSocketContext();
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [finalScores, setFinalScores] = useState<Map<string, GameFinalScores>>(new Map());

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  /**
   * Emit a socket event if connected, otherwise set error.
   * Returns true if emit was successful, false if not connected.
   */
  const emitIfConnected = useCallback(<T,>(event: SocketEvent, data?: T): boolean => {
    if (!socket) {
      setError('Not connected to server');
      return false;
    }
    socket.emit(event, data);
    return true;
  }, [socket]);

  // Ref to track current selectedGame for use in socket handlers (avoids stale closures)
  const selectedGameRef = useRef<Game | null>(null);
  
  // Ref to track if we've done initial game selection
  const hasInitializedSelection = useRef(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedGameRef.current = selectedGame;
  }, [selectedGame]);

  // Select game - joins admin to game room for real-time updates.
  // Games are addressed by database id: room codes are reused once released.
  const selectGame = useCallback((game: Game | null) => {
    // Persist selection to localStorage
    if (game) {
      storeSelectedGameId(game.dbId);
    } else {
      storeSelectedGameId(null);
    }

    if (!socket) {
      setSelectedGame(game);
      return;
    }

    if (game) {
      // Emit to server to join the game room (or open it from the archive)
      const request: SelectGameRequest = { gameId: game.dbId };
      socket.emit(SocketEvent.GAME_SELECT, request);
    }
    // State will be updated when we receive GAME_SELECTED response
    // But also set immediately for responsiveness
    setSelectedGame(game);
  }, [socket]);

  /**
   * Archived game guard. An archived game is outside the live engine and its
   * room code may now belong to another game, so a request addressed by that
   * code would reach the other game. While one is selected, drop any outgoing
   * event whose payload names its room code. Reads that work on archived games
   * address it by `gameId` instead and pass through.
   */
  useEffect(() => {
    if (!socket || !selectedGame?.archived) return;
    const archivedRoomCode = selectedGame.roomCode;
    const originalEmit = socket.emit;
    const guardedEmit = function (this: typeof socket, event: string, ...args: unknown[]) {
      const payload = args[0];
      if (
        payload !== null &&
        typeof payload === 'object' &&
        (payload as { roomCode?: unknown }).roomCode === archivedRoomCode
      ) {
        gameLogger.warn('Blocked room-code request for an archived game', { event, roomCode: archivedRoomCode });
        return this;
      }
      return originalEmit.call(this, event, ...args);
    };
    socket.emit = guardedEmit as typeof socket.emit;
    return () => {
      socket.emit = originalEmit;
    };
  }, [socket, selectedGame?.archived, selectedGame?.roomCode]);

  // Refresh games list
  const refreshGames = useCallback(() => {
    emitIfConnected(SocketEvent.GAME_LIST);
  }, [emitIfConnected]);

  // Create game
  const createGame = useCallback((request: CreateGameRequest) => {
    setError(null);
    setIsLoading(true);
    if (!emitIfConnected(SocketEvent.GAME_CREATE, request)) {
      setIsLoading(false);
    }
  }, [emitIfConnected]);

  // Create round
  const createRound = useCallback((request: CreateRoundRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_CREATE, request);
  }, [emitIfConnected]);

  // Each round action clears the last one's error, so a refusal shows afresh.

  // Start round
  const startRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_START, request);
  }, [emitIfConnected]);

  // End round
  const endRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_END, request);
  }, [emitIfConnected]);

  // Cancel round
  const cancelRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_CANCEL, request);
  }, [emitIfConnected]);

  // Disable round
  const disableRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_DISABLE, request);
  }, [emitIfConnected]);

  // Force results display for completed round
  const forceResults = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_FORCE_RESULTS, request);
  }, [emitIfConnected]);

  // Edit round
  const editRound = useCallback((request: EditRoundRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_EDIT, request);
  }, [emitIfConnected]);

  // Load round
  const loadRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_LOAD, request);
  }, [emitIfConnected]);

  // Unload round
  const unloadRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_UNLOAD, request);
  }, [emitIfConnected]);

  // Delete round (setup-phase rounds only)
  const deleteRound = useCallback((request: RoundActionRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_DELETE, request);
  }, [emitIfConnected]);

  // Update round player
  const updateRoundPlayer = useCallback((request: UpdateRoundPlayerRequest) => {
    setError(null);
    emitIfConnected(SocketEvent.ROUND_UPDATE_PLAYER, request);
  }, [emitIfConnected]);

  // End game
  const endGame = useCallback((request: EndGameRequest) => {
    emitIfConnected(SocketEvent.GAME_END, request);
  }, [emitIfConnected]);

  // Start game (FPR-aware — triggers Discord channel creation + status transition)
  const startGame = useCallback((request: GameStartRequest) => {
    emitIfConnected(SocketEvent.GAME_START, request);
  }, [emitIfConnected]);

  // Delete game permanently (cascades to all rounds, players, registrations)
  const deleteGame = useCallback((request: DeleteGameRequest) => {
    emitIfConnected(SocketEvent.GAME_DELETE, request);
  }, [emitIfConnected]);

  // Toggle client debug visibility
  const toggleClientDebug = useCallback((request: ToggleClientDebugRequest) => {
    emitIfConnected(SocketEvent.TOGGLE_CLIENT_DEBUG, request);
  }, [emitIfConnected]);

  // Update effect settings
  const updateEffectSettings = useCallback((request: UpdateEffectSettingsRequest) => {
    emitIfConnected(SocketEvent.ADMIN_UPDATE_EFFECT_SETTINGS, request);
  }, [emitIfConnected]);

  // Preview an effect on the game room's electron clients
  const previewEffect = useCallback((request: AdminPreviewEffectRequest) => {
    emitIfConnected(SocketEvent.ADMIN_PREVIEW_EFFECT, request);
  }, [emitIfConnected]);

  // Socket event listeners - single source of truth
  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    // Request games when authenticated
    gameLogger.debug('Requesting game list');
    socket.emit(SocketEvent.GAME_LIST);

    // Handle game list response
    const handleGameList = (response: GameListResponse) => {
      gameLogger.debug('Received game list', { games: response.games });
      setGames(response.games);
      
      // Update selected game if it exists in the new list
      const currentSelected = selectedGameRef.current;
      if (currentSelected) {
        const updatedGame = response.games.find(g => g.id === currentSelected.id);
        if (updatedGame) {
          setSelectedGame(updatedGame);
        }
      }
      
      // Auto-select game on first load (only once)
      if (!hasInitializedSelection.current && response.games.length > 0) {
        hasInitializedSelection.current = true;

        // Restore from localStorage — but only if the saved game still exists
        // and is ongoing (non-terminal). Finished/abandoned/stale saved games
        // start blank. There is no most-recent auto-select fallback: the
        // default state is no game loaded.
        const savedGameId = readSelectedGameId();
        if (savedGameId !== null) {
          const savedGame = response.games.find(g => g.dbId === savedGameId);
          const isOngoing = !!savedGame
            && !isTerminal(savedGame.gameType, savedGame.status);
          if (savedGame && isOngoing) {
            gameLogger.debug('Restoring ongoing game from localStorage', { gameId: savedGameId, roomCode: savedGame.roomCode });
            socket.emit(SocketEvent.GAME_SELECT, { gameId: savedGame.dbId } satisfies SelectGameRequest);
            setSelectedGame(savedGame);
          } else {
            // finished / abandoned / no longer listed → start blank
            storeSelectedGameId(null);
          }
        }
      }
    };

    // Handle game created
    const handleGameCreated = (response: CreateGameResponse) => {
      setIsLoading(false);
      if (response.success && response.game) {
        gameLogger.info('Game created', { gameId: response.game.id, roomCode: response.game.roomCode });
        // Add the new game to our games list immediately
        setGames(prev => [...prev, response.game!]);
        // Auto-select the new game
        setSelectedGame(response.game);
        // Also emit GAME_SELECT to join the room (server already joined us, but this ensures sync)
        socket.emit(SocketEvent.GAME_SELECT, { gameId: response.game.dbId } satisfies SelectGameRequest);
        // Save to localStorage
        storeSelectedGameId(response.game.dbId);
      } else {
        setError(response.error || 'Failed to create game');
      }
    };

    // Handle player joined (updates game player list)
    const handlePlayerJoined = (response: JoinGameResponse) => {
      if (!response.success) {
        setError(response.error || 'Failed to join game');
      }
    };

    // Handle round created
    const handleRoundCreated = (response: CreateRoundResponse) => {
      if (!response.success) {
        setError(response.error || 'Failed to create round');
      }
    };

    // Handle round updates (started, ended, cancelled, disabled)
    const handleRoundUpdated = (response: RoundActionResponse) => {
      if (!response.success) {
        setError(response.error || 'Failed to update round');
      }
    };

    // Handle player role updates
    const handlePlayerUpdated = (response: UpdateRoundPlayerResponse) => {
      if (!response.success) {
        setError(response.error || 'Failed to update player');
      }
    };

    // Handle game selected (admin joined game room) — initial selection only;
    // ongoing state changes flow through GAME_STATE_UPDATED.
    const handleGameSelected = (response: SelectGameResponse) => {
      if (response.success && response.game) {
        setSelectedGame(response.game);
        gameLogger.info('Admin joined game room', { roomCode: response.game.roomCode });
      } else if (!response.success) {
        setError(response.error || 'Failed to select game');
      }
    };

    // Handle game started (FPR GAME_START — error path only; state flows via GAME_STATE_UPDATED)
    const handleGameStarted = (response: GameStartResponse) => {
      if (!response.success) {
        setError(response.error || 'Failed to start game');
      }
    };

    // Handle game ended — keep finalScores capture; state flows via GAME_STATE_UPDATED
    const handleGameEnded = (response: EndGameResponse) => {
      if (response.success) {
        const { gameId, finalScores } = response;
        if (gameId && finalScores) {
          setFinalScores(prev => new Map(prev).set(gameId, finalScores));
        }
      } else {
        setError(response.error || 'Failed to end game');
      }
    };

    // Handle client debug toggled — not yet routed through GAME_STATE_UPDATED;
    // server emits CLIENT_DEBUG_TOGGLED with only roomCode/show.
    const handleClientDebugToggled = (response: ToggleClientDebugResponse) => {
      if (response.success && response.roomCode && response.show !== undefined) {
        gameLogger.debug('Client debug toggled', { roomCode: response.roomCode, show: response.show });
        // Live-only event addressed by room code: archived games are never the target.
        setGames(prev => prev.map(g =>
          g.roomCode === response.roomCode && !g.archived ? { ...g, showClientDebug: response.show } : g
        ));
        if (selectedGameRef.current?.roomCode === response.roomCode && !selectedGameRef.current.archived) {
          setSelectedGame({ ...selectedGameRef.current, showClientDebug: response.show! });
        }
      }
    };

    // Handle game deleted
    const handleGameDeleted = (response: DeleteGameResponse) => {
      if (response.success) {
        setGames(prev => prev.filter(g => g.dbId !== response.gameId));
        if (selectedGameRef.current?.dbId === response.gameId) {
          setSelectedGame(null);
          storeSelectedGameId(null);
        }
        gameLogger.info('Game deleted', { gameId: response.gameId });
      } else {
        setError(response.error || 'Failed to delete game');
      }
    };

    // Single chokepoint: every Game state mutation flows through here.
    // Replaces ~14 piggyback handlers that each ran setGames + setSelectedGame.
    const handleGameStateUpdated = (broadcast: GameStateUpdatedBroadcast) => {
      setGames(prev => {
        const known = prev.some(g => g.id === broadcast.game.id);
        if (!known) return prev;
        return prev.map(g => g.id === broadcast.game.id ? broadcast.game : g);
      });
      if (selectedGameRef.current?.id === broadcast.game.id) {
        setSelectedGame(broadcast.game);
      }
      gameLogger.debug('Game state updated', { source: broadcast.source, roomCode: broadcast.roomCode });
    };

    /**
     * Re-join the selected game's socket room after a reconnect.
     *
     * A reconnect hands us a new socket id that is not in `game:<roomCode>`, and
     * every server push — the per-viewer masked snapshot, the admin-only player
     * locations — is room-scoped, so the dashboard silently goes stale. Nothing
     * else re-runs the selection: `hasInitializedSelection` and `isAuthenticated`
     * both survive the drop, so neither GAME_LIST nor GAME_SELECT fires again.
     *
     * Keyed on AUTH_SUCCESS rather than the reconnect event because GAME_SELECT is
     * rejected until authentication has registered the client on the new socket.
     * A first login has no selection yet, so this is a no-op there.
     */
    const handleAuthSuccess = () => {
      const selected = selectedGameRef.current;
      if (!selected) return;
      // A finished game has no live updates to miss, and it may already have aged
      // out of the server's memory — in which case GAME_SELECT answers "Game not
      // found" and we would raise an error over a results view that is fine.
      if (isTerminal(selected.gameType, selected.status)) return;
      gameLogger.info('Re-selecting game after reconnect', { gameId: selected.dbId, roomCode: selected.roomCode });
      socket.emit(SocketEvent.GAME_SELECT, { gameId: selected.dbId } satisfies SelectGameRequest);
    };

    // Register all listeners
    socket.on(SocketEvent.AUTH_SUCCESS, handleAuthSuccess);
    socket.on(SocketEvent.GAME_LIST, handleGameList);
    socket.on(SocketEvent.GAME_CREATED, handleGameCreated);
    socket.on(SocketEvent.GAME_SELECTED, handleGameSelected);
    socket.on(SocketEvent.GAME_STARTED, handleGameStarted);
    socket.on(SocketEvent.GAME_ENDED, handleGameEnded);
    socket.on(SocketEvent.GAME_JOINED, handlePlayerJoined);
    socket.on(SocketEvent.GAME_PLAYER_JOINED, handlePlayerJoined);
    socket.on(SocketEvent.GAME_PLAYER_LEFT, handlePlayerJoined);
    socket.on(SocketEvent.GAME_PLAYER_DISCONNECTED, handlePlayerJoined);
    socket.on(SocketEvent.ROUND_CREATED, handleRoundCreated);
    socket.on(SocketEvent.ROUND_STARTED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_ENDED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_CANCELLED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_DISABLED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_EDITED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_LOADED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_UNLOADED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_DELETED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_FORCE_RESULTS_UPDATED, handleRoundUpdated);
    socket.on(SocketEvent.ROUND_PLAYER_UPDATED, handlePlayerUpdated);
    socket.on(SocketEvent.CLIENT_DEBUG_TOGGLED, handleClientDebugToggled);
    const offGameState = onGameStateUpdated(socket, handleGameStateUpdated);
    socket.on(SocketEvent.GAME_DELETED, handleGameDeleted);

    // Cleanup
    return () => {
      socket.off(SocketEvent.GAME_LIST, handleGameList);
      socket.off(SocketEvent.GAME_CREATED, handleGameCreated);
      socket.off(SocketEvent.GAME_SELECTED, handleGameSelected);
      socket.off(SocketEvent.GAME_STARTED, handleGameStarted);
      socket.off(SocketEvent.GAME_ENDED, handleGameEnded);
      socket.off(SocketEvent.GAME_JOINED, handlePlayerJoined);
      socket.off(SocketEvent.GAME_PLAYER_JOINED, handlePlayerJoined);
      socket.off(SocketEvent.GAME_PLAYER_LEFT, handlePlayerJoined);
      socket.off(SocketEvent.GAME_PLAYER_DISCONNECTED, handlePlayerJoined);
      socket.off(SocketEvent.ROUND_CREATED, handleRoundCreated);
      socket.off(SocketEvent.ROUND_STARTED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_ENDED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_CANCELLED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_DISABLED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_EDITED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_LOADED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_UNLOADED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_DELETED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_FORCE_RESULTS_UPDATED, handleRoundUpdated);
      socket.off(SocketEvent.ROUND_PLAYER_UPDATED, handlePlayerUpdated);
      socket.off(SocketEvent.CLIENT_DEBUG_TOGGLED, handleClientDebugToggled);
      offGameState();
      socket.off(SocketEvent.GAME_DELETED, handleGameDeleted);
      socket.off(SocketEvent.AUTH_SUCCESS, handleAuthSuccess);
    };
  }, [socket, isAuthenticated]);

  const value = useMemo(() => ({
    games,
    selectedGame,
    selectGame,
    createGame,
    createRound,
    startRound,
    endRound,
    cancelRound,
    disableRound,
    forceResults,
    editRound,
    loadRound,
    unloadRound,
    deleteRound,
    updateRoundPlayer,
    endGame,
    startGame,
    deleteGame,
    refreshGames,
    error,
    isLoading,
    clearError,
    finalScores,
    toggleClientDebug,
    updateEffectSettings,
    previewEffect,
  }), [
    games,
    selectedGame,
    selectGame,
    createGame,
    createRound,
    startRound,
    endRound,
    cancelRound,
    disableRound,
    forceResults,
    editRound,
    loadRound,
    unloadRound,
    deleteRound,
    updateRoundPlayer,
    endGame,
    startGame,
    deleteGame,
    refreshGames,
    error,
    isLoading,
    clearError,
    finalScores,
    toggleClientDebug,
    updateEffectSettings,
    previewEffect,
  ]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// Custom hook for consuming context
export function useGameContext() {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
}
